import React from "react";
import * as I from "../types";
import * as BTN from "./Button";
import * as BS from "react-bootstrap";
import * as H from "../../../../hooks";
import * as C from "../../../../Common";
import { T, TB, TC } from "../../../../Constants";

export type Props = Pick<BTN.Props, "action">;

export type FilterModel = Status[];
type Status = RowRem["action_status"];
type RowRem = ReturnType<T.API.EDL.GetRemarquesItems>[number];

const STATUS = [
    { value: "none", label: TC.GRID_TICKET_FILTER_NONE, variant: "primary", order: 0 },
    { value: "some_open", label: TC.GRID_TICKET_FILTER_SOME_OPEN, variant: "warning", order: 1 },
    { value: "all_closed", label: TC.GRID_TICKET_FILTER_ALL_CLOSED, variant: "success", order: 2 },
] as T.Option<Record<"variant", T.ColorTypes> & Record<"order", number>, Status>[];

export const type = "agCustomRemTickets";

export const sort: I.Comparator<unknown, RowRem> = (a, b, node_a, node_b) => {
    const a_order = STATUS.find(o => o.value === node_a?.data?.action_status)?.order || 0;
    const b_order = STATUS.find(o => o.value === node_b?.data?.action_status)?.order || 0;
    if (a_order !== b_order) return a_order - b_order;
    else return (node_a?.data?.actions || 0) - (node_b?.data?.actions || 0);
}

export const Cell = React.forwardRef<object, I.CellProps<Props>>((props, ref) => {
    const value = React.useMemo<RowRem>(() => props?.data || null, [props.data]);

    const style = React.useMemo(() => {
        if (!value?.action_status) return null;

        const callback = props?.colDef?.params?.action;
        const children = TB.getNumber(value?.actions, 0);
        const status = STATUS.find(s => s.value === value?.action_status);

        const button_style: C.ButtonProps = { children, variant: status?.variant, size: "sm" };
        if (value?.action_status === "none") button_style.disabled = true;
        if (typeof callback === "function") button_style.onClick = () => callback(value, props.node, props);
        return button_style;
    }, [value, props]);

    if (!style) return null;
    return <div className="w-100 text-center m-0" children={<C.Button {...style} />} />;
});

export const Filter: React.FC<I.FilterProps<FilterModel>> = ({ onModelChange, getValue, ...props }) => {
    const lg = H.useLanguage();

    const doesFilterPass = React.useCallback<I.FilterAction["doesFilterPass"]>(params => {
        // Filter is null (= inactive), or nothing set as filtered, so everything pass
        if (!Array.isArray(props.model) || props.model.length === 0) return true;
        // Get the cell's value
        let value: RowRem = params.node?.data;
        // Value is empty
        if (!value?.action_status) return !props.model.includes("none");
        // Value is one of the selected status
        else return !props.model.includes(value.action_status);
    }, [props.model, getValue]);

    const toggle_status = React.useCallback((status: Status) => {
        let new_model = props.model || [];
        if (new_model.includes(status)) new_model = new_model.filter(x => x !== status);
        else new_model = new_model.concat(status);
        // No statuses are selected, so filter is disabled
        if (new_model.length === 0) new_model = null;
        // Update the model
        onModelChange(new_model);
    }, [props.model, onModelChange]);

    I.useGridFilter({ doesFilterPass });

    return <div className='p-2'>
        {STATUS.map(o => <BS.InputGroup key={o.value} className="align-items-center mb-1">
            <BS.Form.Check checked={!props.model?.includes?.(o.value)} onChange={() => toggle_status(o.value)} />
            <span className='ml-3 flex-grow-1 pointer' onClick={() => toggle_status(o.value)}>{o.label ? lg.getStaticText(o.label) : null}</span>
        </BS.InputGroup>)}
    </div>;
};

Cell.displayName = "AG_TicketButton_Cell";